import React, { useMemo } from 'react';
import { useWidgetBuilder } from '../context/WidgetBuilderContext';
import { useBuilderLabels, useBuilderLocale } from '../context/BuilderLocaleContext';
import {
  FEATURED_MATCH_TYPES,
  getTranslatedWidgetCategories,
  normalizeWidgetType,
  PLAYER_PICKER_TYPES,
  TEAM_PICKER_TYPES,
  THEMES,
} from './widgetTypes';
function formatSavedTime(iso, locale) {
  if (!iso) return null;
  return new Date(iso).toLocaleString(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function SaveStatusLine({ saveStatus, saveMessage, lastSavedAt, storageMode, t, locale }) {
  if (saveStatus === 'pending' || saveStatus === 'saving') {
    return (
      <p className="mt-2 text-xs font-medium text-slate-500 animate-pulse">
        {saveStatus === 'saving' ? t('saving') : t('unsavedChanges')}
      </p>
    );
  }
  if (saveStatus === 'error') {
    return (
      <p className="mt-2 text-xs font-medium text-red-700">{t('saveError')}</p>
    );
  }
  if (saveMessage === 'saved') {
    return (
      <p className="mt-2 text-xs font-medium text-brand-700">
        {storageMode === 'cloud' ? t('savedCloud') : t('savedLocal')}
      </p>
    );
  }
  if (saveStatus === 'saved' && lastSavedAt) {
    return (
      <p className="mt-2 text-xs text-slate-500">
        {t('autoSaved', { time: formatSavedTime(lastSavedAt, locale) })}
      </p>
    );
  }
  return <p className="mt-2 text-xs text-slate-400">{t('changesAutoSave')}</p>;
}

const THEME_LABEL_KEYS = { light: 'themeLight', dark: 'themeDark' };

export default function ConfigPanel() {
  const t = useBuilderLabels();
  const { locale: uiLocale } = useBuilderLocale();
  const {
    config,
    updateConfig,
    widgetId,
    lastSavedAt,
    saveMessage,
    saveStatus,
    storageMode,
    saveCurrentWidget,
  } = useWidgetBuilder();
  const widgetType = normalizeWidgetType(config.type);
  const showMatchPicker = FEATURED_MATCH_TYPES.has(widgetType);
  const showTeamPicker = TEAM_PICKER_TYPES.has(widgetType);
  const showPlayerPicker = PLAYER_PICKER_TYPES.has(widgetType);
  const categories = useMemo(() => getTranslatedWidgetCategories(t), [t, uiLocale]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-5 space-y-6">
        <div>
          <h2 className="text-lg font-bold text-slate-900">{t('configure')}</h2>
          <p className="text-sm text-slate-500">{t('changesInstant')}</p>
          <SaveStatusLine
            saveStatus={saveStatus}
            saveMessage={saveMessage}
            lastSavedAt={lastSavedAt}
            storageMode={storageMode}
            t={t}
            locale={uiLocale}
          />
          <button
            type="button"
            onClick={() => saveCurrentWidget()}
            className="mt-3 w-full py-2.5 rounded-lg text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 transition-colors"
          >
            {t('saveNow')}
          </button>
          <p className="mt-1 text-[10px] text-slate-400 font-mono truncate" title={widgetId}>
            {t('widgetId')}: {widgetId}
          </p>
        </div>

        <label className="block">
          <span className="text-xs font-semibold uppercase text-slate-500">{t('widgetName')}</span>
          <input
            type="text"
            value={config.name}
            onChange={(e) => updateConfig({ name: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none"
          />
        </label>

        {showMatchPicker && (
          <p className="text-xs text-slate-600 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2">
            {t('hintSelectMatchBar')}
          </p>
        )}

        <div className="space-y-4">
          <p className="text-xs font-semibold uppercase text-slate-500">{t('widgetType')}</p>
          {categories.map((cat) => (
            <fieldset key={cat.id}>
              <legend className="text-[10px] font-bold uppercase text-slate-400 mb-2">
                {cat.label}
              </legend>
              <div className="space-y-2">
                {cat.types.map((wt) => {
                  const active = normalizeWidgetType(config.type) === wt.id;
                  return (
                    <label
                      key={wt.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        active
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="widgetType"
                        checked={active}
                        onChange={() =>
                          updateConfig({
                            type: wt.id,
                            featuredMatchId:
                              wt.id !== normalizeWidgetType(config.type)
                                ? ''
                                : config.featuredMatchId,
                            selectedTeam: (() => {
                              const teamWidgets = new Set([
                                'team-profile',
                                'injuries',
                                'player-list',
                              ]);
                              const prev = normalizeWidgetType(config.type);
                              if (!teamWidgets.has(wt.id)) return '';
                              return teamWidgets.has(prev) ? config.selectedTeam : '';
                            })(),
                            selectedPlayer:
                              wt.id !== 'player-profile' ? '' : config.selectedPlayer,
                          })
                        }
                        className="mt-1"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{wt.label}</span>
                        <span className="block text-xs text-slate-500">{wt.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ))}
        </div>

        {showTeamPicker && (
          <p className="text-xs text-slate-500 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            {widgetType === 'injuries'
              ? t('hintTeamInjuries')
              : widgetType === 'player-list'
                ? t('hintTeamPlayerList')
                : t('hintTeamProfile')}
          </p>
        )}

        {showPlayerPicker && (
          <p className="text-xs text-slate-500 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            {t('hintPlayerPicker')}
          </p>
        )}

        <fieldset>
          <legend className="text-xs font-semibold uppercase text-slate-500 mb-2">
            {t('theme')} <span className="text-red-500">*</span>
          </legend>
          <div className="grid grid-cols-2 gap-2">
            {THEMES.map((theme) => (
              <button
                key={theme.id}
                type="button"
                onClick={() => updateConfig({ theme: theme.id })}
                className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${
                  config.theme === theme.id
                    ? 'border-brand-600 bg-brand-600 text-white'
                    : 'border-slate-200 text-slate-700 hover:border-slate-300'
                }`}
              >
                {t(THEME_LABEL_KEYS[theme.id] || theme.id)}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">{t('themeRequired')}</p>
        </fieldset>

        <label className="block">
          <span className="text-xs font-semibold uppercase text-slate-500">{t('allowedDomains')}</span>
          <textarea
            value={(config.allowedDomains || []).join('\n')}
            onChange={(e) =>
              updateConfig({
                allowedDomains: e.target.value
                  .split(/[\n,]+/)
                  .map((d) => d.trim())
                  .filter(Boolean),
              })
            }
            rows={3}
            placeholder={t('domainsPlaceholder')}
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
          />
          <p className="text-xs text-slate-400 mt-1">{t('allowedDomainsHint')}</p>
        </label>

        <label className="block">
          <span className="text-xs font-semibold uppercase text-slate-500">{t('accentColor')}</span>
          <div className="mt-1 flex gap-2">
            <input
              type="color"
              value={`#${config.primaryColor}`}
              onChange={(e) =>
                updateConfig({ primaryColor: e.target.value.replace('#', '') })
              }
              className="h-10 w-14 rounded border border-slate-200 cursor-pointer"
            />
            <input
              type="text"
              value={config.primaryColor}
              onChange={(e) => updateConfig({ primaryColor: e.target.value.replace('#', '') })}
              className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
            />
          </div>
        </label>
      </div>
    </div>
  );
}
